class FilterChainBuilder {
    filters = [];
    _family;
    constructor() {
        this.filters = [];
    }
    static builder() {
        return new FilterChainBuilder();
    }
    clean() {
        return new FilterChainBuilder();
    }
    addFilter(filter) {
        if (isFilter(filter)) {
            this.filters.push(filter);
        }
        return this;
    }
    addFilters(filters) {
        if (filters) {
            this.filters = this.filters.concat(this.sanitizeFilters(filters));
        }
        return this;
    }
    withFilters(filters) {
        this.filters = this.sanitizeFilters(filters);
        return this;
    }
    sanitizeFilters(filters) {
        return filters
            .filter(entry => isFilter(entry))
            .map(entry => entry);
    }
    family(family) {
        this._family = family;
        return this;
    }
    build() {
        //Inverse order
        let filters = this.filters.sort((f1, f2) => f2.order() - f1.order());
        if (this._family) {
            filters = filters.filter(filter => filter.family() === this._family);
        }
        let chain = new FilterChain(new EndOfChain());
        for (const filter of filters) {
            chain = new FilterChain(filter, chain);
        }
        return chain;
    }
}
class Filter {
    config;
    init(filterConfig) {
        this.config = filterConfig;
    }
    family() {
        if (this.config && this.config.family) {
            return this.config.family;
        }
        return "orphan";
    }
    order() {
        if (this.config && (this.config.order || this.config.order === 0)) {
            return this.config.order;
        }
        return Number.MAX_SAFE_INTEGER;
    }
}
class FilterChain {
    filter;
    chain;
    constructor(filter, chain) {
        this.filter = filter;
        this.chain = chain;
    }
    async doFilter(request, response) {
        return this.filter.doFilter(request, response, this.chain);
    }
}
class EndOfChain extends Filter {
    /* istanbul ignore next */
    name() {
        return "End";
    }
    async doFilter() {
        //Do nothing. It's the end of the chain
        return Promise.resolve();
    }
}
//@ts-ignore this function aims to verify is whatever object is a filter, for that reason we have to accept any
function isFilter(object) {
    if (!object) {
        return false;
    }
    if (!('init' in object || 'order' in object || 'family' in object || 'doFilter' in object)) {
        return false;
    }
    return typeof object.init === 'function' &&
        typeof object.order === 'function' &&
        typeof object.family === 'function' &&
        typeof object.doFilter === 'function';
}

class Registry {
    registry = {};
    firstEntryDate;
    isDelayed(exportIntervalInMs) {
        return this.firstEntryDate !== undefined &&
            ((new Date()).getTime() - this.firstEntryDate.getTime()) >= exportIntervalInMs;
    }
    getMetricRegistry() {
        return this.registry;
    }
}

class Metric {
    name;
    key;
    labels;
    options;
    value;
    constructor(value) {
        this.value = value;
    }
    create(name, labels) {
        if (!name) {
            return this;
        }
        let metricKey = name;
        if (labels) {
            const orderedLabels = Object.keys(labels).sort((a, b) => a.localeCompare(b));
            for (const label of orderedLabels) {
                metricKey = `${metricKey}_${label}=${labels[label]}`;
            }
            this.labels = labels;
        }
        //TODO Reduce the size of the key
        this.key = btoa(metricKey);
        this.name = name;
        return this;
    }
    record(value) {
        this.value.record(value);
        return this;
    }
    merge(metric) {
        if (metric.constructor != this.constructor) {
            console.debug(`Metric [${metric.getName()}] can't be merged because it has a different type.`);
            return this;
        }
        if (metric.getIdentifier() != this.getIdentifier()) {
            console.debug(`Metric [${metric.getName()}] can't be merged because identifiers don't match.`);
            return this;
        }
        this.value.merge(metric.getValue());
        this.mergeOptions(metric.getOptions());
        return this;
    }
    mergeOptions(options) {
        if (options === undefined) {
            return;
        }
        if (!this.options) {
            this.options = options;
            return;
        }
        if (this.options.description === undefined && options.description !== undefined) {
            this.options.description = options.description;
        }
        if (this.options.system === undefined && options.system !== undefined) {
            this.options.system = options.system;
        }
        if (this.options.unit === undefined && options.unit !== undefined) {
            this.options.unit = options.unit;
        }
        //Note: Histogram boundaries cannot be merged because it affects the boundaries
    }
    getValue() {
        return this.value;
    }
    setOptions(options) {
        this.options = options;
        return this;
    }
    getOptions() {
        return this.options;
    }
    getIdentifier() {
        return this.key;
    }
    getName() {
        return this.name;
    }
    /* istanbul ignore next */
    getLabels() {
        return this.labels;
    }
}

class ObservabilityService {
    version;
    exporters;
    config;
    registry;
    publisherScheduler;
    constructor() {
        document.onvisibilitychange = () => {
            if (document.visibilityState === 'hidden') {
                this.forceExport();
            }
        };
        this.version = '0.4.0'; //pkg.version;
    }
    static getInstance() {
        if (!window.$$O11Y) {
            window.$$O11Y = new ObservabilityService();
        }
        return window.$$O11Y;
    }
    addExporter(exporter) {
        if (!this.exporters) {
            this.exporters = [];
        }
        this.exporters?.push(exporter);
        this.providerFactory();
    }
    recordCounter(metricName, amount, labels, options) {
        this.getRegistry().record('counter', metricName, amount, labels, this.appendSystemInfoToOptions(options));
        this.forceExportIfDelayed();
    }
    recordGauge(metricName, amount, labels, options) {
        this.getRegistry().record('gauge', metricName, amount, labels, this.appendSystemInfoToOptions(options));
        this.forceExportIfDelayed();
    }
    recordHistogram(metricName, amount, labels, options) {
        this.getRegistry().record('histogram', metricName, amount, labels, this.appendSystemInfoToOptions(options));
        this.forceExportIfDelayed();
    }
    forceExport() {
        this.export();
    }
    forceExportIfDelayed() {
        if (this.getRegistry().isDelayed((this.config === undefined) ? 1 : this.config.exportIntervalMillis)) {
            this.export();
        }
        else if (this.config !== undefined && this.publisherScheduler === undefined) {
            this.publisherScheduler = setTimeout(() => this.forceExportIfDelayed(), this.config.exportIntervalMillis);
        }
    }
    export() {
        if (this.exporters === undefined || this.exporters.length == 0) {
            console.warn('ObservabilitySDK :: No exporters available :: Dropping registry');
            this.createRegistry();
            this.publisherScheduler = undefined;
            return;
        }
        for (const exporter of this.exporters) {
            try {
                exporter.publish(this.getRegistry());
            }
            catch (e) {
                console.error(`ObservabilitySDK :: Fail to export metric to exporter`, e);
            }
        }
        this.createRegistry();
        this.publisherScheduler = undefined;
    }
    getRegistry() {
        if (!this.registry) {
            this.createRegistry();
        }
        if (this.registry) {
            return this.registry;
        }
        throw new Error("ObservabilitySDK :: Registry Builder haven't been configured");
    }
    createRegistry() {
        if (this.config && this.config.registryBuilder) {
            this.registry = this.config.registryBuilder();
        }
    }
    appendSystemInfoToOptions(options) {
        return {
            ...options,
            ...{
                system: {
                    name: this.getSystemName(),
                    version: this.getVersion()
                }
            }
        };
    }
    getSystemName() {
        return this.getSystemInfo()?.name || 'com.superbet.web.default';
    }
    getVersion() {
        return this.getSystemInfo()?.version || this.getWebAppVersion();
    }
    getSystemInfo() {
        return this.config?.system;
    }
    getWebAppVersion() {
        return window.superbetVersion || 'v0.0.0';
    }
    setConfig(config) {
        this.config = config;
        this.providerFactory();
    }
    providerFactory() {
        if ((!this.exporters && !this.config) || !this.exporters) {
            return;
        }
    }
}
const ObservabilitySDK = ObservabilityService.getInstance();

function isArrayEquals(a, b) {
    if (a === b)
        return true;
    if (a == null || b == null)
        return false;
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; ++i) {
        if (a[i] !== b[i])
            return false;
    }
    return true;
}

class HistogramMetricValue {
    registeredAt = new Date();
    count = 0;
    sum = 0;
    min;
    max = 0;
    buckets = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    boundaries = [0, 5, 10, 25, 50, 75, 100, 250, 500, 750, 1000, 2500, 5000, 7500, 10000];
    record(value) {
        if (value <= 0) {
            console.debug(`Ignoring instrumentation of metric. Histograms doesn't support values equals or smaller than 0.`);
            return this;
        }
        this.increaseCounter();
        this.updateMax(value);
        this.updateMin(value);
        this.updateSum(value);
        this.updateBuckets(value);
        return this;
    }
    merge(metric) {
        if (metric.count === 0) {
            return this;
        }
        if (!isArrayEquals(this.boundaries, metric.boundaries)) {
            return this;
        }
        this.count = this.count + metric.count;
        this.sum = this.sum + metric.sum;
        this.updateMax(metric.max);
        if (metric.min) {
            this.updateMin(metric.min);
        }
        this.mergeBuckets(metric.buckets);
        return this;
    }
    increaseCounter() {
        this.count++;
    }
    updateMax(value) {
        if (value > this.max) {
            this.max = value;
        }
    }
    updateMin(value) {
        if (this.min === undefined) {
            this.min = value;
            return;
        }
        if (value < this.min) {
            this.min = value;
        }
    }
    updateSum(value) {
        this.sum = this.sum + value;
    }
    updateBuckets(value) {
        if (!this.boundaries) {
            return;
        }
        for (let index = 0; index <= this.boundaries.length; index++) {
            if (index == this.boundaries.length) {
                this.buckets[index]++;
                break;
            }
            if (this.boundaries[index] >= value) {
                this.buckets[index]++;
                break;
            }
        }
    }
    mergeBuckets(buckets) {
        for (let index = 0; index < this.buckets.length; index++) {
            if (buckets[index] > 0) {
                this.buckets[index] = this.buckets[index] + buckets[index];
            }
        }
    }
    isInitialized() {
        return this.count > 0;
    }
    getRegisteredAt() {
        return this.registeredAt;
    }
    getCount() {
        return this.count;
    }
    getSum() {
        return this.sum;
    }
    getMin() {
        return this.min;
    }
    getMax() {
        return this.max;
    }
    getBuckets() {
        return this.buckets;
    }
    getBoundaries() {
        return this.boundaries;
    }
    setBoundaries(boundaries) {
        if (!boundaries || boundaries.length === 0) {
            return;
        }
        this.boundaries = boundaries.sort((n1, n2) => n1 - n2);
        this.buckets = new Array(this.boundaries.length + 1).fill(0);
    }
}

class SimpleMetricValue {
    registeredAt = new Date();
    value;
    constructor(initialValue) {
        this.value = initialValue;
    }
    record(value) {
        if (!this.value) {
            this.value = 0;
        }
        this.value = this.value + value;
        return this;
    }
    merge(metric) {
        if (!this.isInitialized() && metric.isInitialized()) {
            return metric;
        }
        if (this.isInitialized() && !metric.isInitialized()) {
            return this;
        }
        this.record(metric.getValue());
        return this;
    }
    getRegisteredAt() {
        return this.registeredAt;
    }
    isInitialized() {
        return this.value !== undefined;
    }
    getValue() {
        return this.value;
    }
}

class ConfigMerger {
    overrideNestedIfNotEmpty(attribute, childrenAttributes, current, loaded) {
        for (const key of childrenAttributes) {
            //@ts-expect-error
            this.overrideIfNotEmpty(key, current[attribute], loaded[attribute]);
        }
    }
    overrideIfNotEmpty(attribute, current, loaded) {
        //@ts-expect-error
        if (loaded && attribute in loaded && !!loaded[attribute]) {
            //@ts-expect-error
            current[attribute] = loaded[attribute];
        }
    }
}

class HistogramMetric extends Metric {
    constructor() {
        super(new HistogramMetricValue());
    }
    setBoundaries(boundaries) {
        if (!boundaries || boundaries.length == 0) {
            return this;
        }
        if (this.value.getCount() > 0) {
            return this;
        }
        this.value.setBoundaries(boundaries);
        return this;
    }
    setOptions(options) {
        if ("boundaries" in options) {
            this.setBoundariesBasedOnOptions(options);
        }
        return super.setOptions(options);
    }
    setBoundariesBasedOnOptions(options) {
        if (options.boundaries && options.boundaries.length > 0) {
            this.setBoundaries(options.boundaries);
        }
    }
    getType() {
        return 'histogram';
    }
}

class GaugeMetric extends Metric {
    constructor() {
        super(new SimpleMetricValue(0));
    }
    getType() {
        return 'gauge';
    }
}

class CounterMetric extends Metric {
    constructor() {
        super(new SimpleMetricValue());
    }
    record(value) {
        if (value <= 0) {
            console.debug(`Ignoring instrumentation of metric [${this.getName()}]. Counter doesn't support values equals or smaller than 0.`);
            return this;
        }
        return super.record(value);
    }
    getType() {
        return 'counter';
    }
}

class SimpleRegistry extends Registry {
    record(type, metricName, amount, labels, options) {
        const metric = this.metricFactory(type)
            .create(metricName, labels);
        if (options) {
            metric.setOptions(options);
        }
        const metricKey = metric.getIdentifier();
        if (!metricKey) {
            return;
        }
        metric.record(amount);
        const existingMetric = this.registry[metricKey];
        if (existingMetric) {
            this.mergeMetric(existingMetric, metric);
        }
        else if (metric.getValue().isInitialized()) {
            this.addMetric(metricKey, metric);
        }
    }
    metricFactory(type) {
        if (type === 'histogram') {
            return new HistogramMetric();
        }
        if (type === 'gauge') {
            return new GaugeMetric();
        }
        return new CounterMetric();
    }
    mergeMetric(existingMetric, metric) {
        existingMetric.merge(metric);
    }
    addMetric(metricKey, metric) {
        this.registry[metricKey] = metric;
        this.setFirstEntryDate();
    }
    setFirstEntryDate() {
        if (!this.firstEntryDate) {
            this.firstEntryDate = new Date();
        }
    }
}

class DynamicHook {
    filters = [];
    chain;
    static getInstance() {
        if (!window.$$O11Y$$DynamicHook) {
            window.$$O11Y$$DynamicHook = new DynamicHook();
        }
        return window.$$O11Y$$DynamicHook;
    }
    static addFilter(filter) {
        DynamicHook.getInstance().addFilter(filter);
    }
    addFilter(filter) {
        this.filters = this.filters.filter(f => f.name() !== filter.name());
        this.filters.push(filter);
        this.createChain();
    }
    createChain() {
        this.chain = FilterChainBuilder.builder()
            .withFilters(this.filters)
            .addFilter(new DynamicHookDispatcherFilter())
            .build();
    }
    record(key, amount, attributes, options) {
        if (this.chain) {
            return this.chain.doFilter({
                key: key,
                amount: amount,
                attributes: attributes || {},
                options: options || {},
                context: {}
            }, {});
        }
        return Promise.resolve();
    }
}
class DynamicHookDispatcherFilter extends Filter {
    constructor() {
        super();
    }
    family() {
        return 'hooks';
    }
    order() {
        return Number.MAX_SAFE_INTEGER;
    }
    name() {
        /* istanbul ignore next */
        return 'DynamicHookFilter';
    }
    async doFilter(request, response, chain) {
        if (request.type === 'histogram') {
            ObservabilitySDK.recordHistogram(request.key, request.amount, request.attributes, request.options);
        }
        else if (request.type === 'gauge') {
            ObservabilitySDK.recordGauge(request.key, request.amount, request.attributes, request.options);
        }
        else {
            //FIXME Default type must be replaced by log once it get implemented
            ObservabilitySDK.recordCounter(request.key, request.amount || 1, request.attributes, request.options);
        }
        return await chain.doFilter(request, response);
    }
}

const DEFAULT_MAX_LENGTH = 255;
const DEFAULT_MIN_LENGTH = 1;
class DataValidationFilter extends Filter {
    validationConfig;
    name() {
        /* istanbul ignore next */
        return 'DataValidationFilter';
    }
    async doFilter(request, response, chain) {
        if (this.validationConfig && request.key) {
            const attributes = request.attributes;
            const invalidAttributes = [];
            const types = this.validationConfig.resolver(request.key);
            if ((!types || types.length == 0) && (!attributes || Object.keys(attributes).length === 0)) {
                return await chain.doFilter(request, response);
            }
            for (const type of types) {
                try {
                    if (!type.type || !(type.type.toLowerCase() === 'string' || type.type.toLowerCase() === 'number' || type.type.toLowerCase() === 'enum' || type.type.toLowerCase() === 'boolean')) {
                        console.warn(`ObservabilitySDK :: Data type [${type.type}] defined for action key[${request.key}] is invalid. Ignoring validation`);
                        continue;
                    }
                    if (!VALIDATORS[type.type](type, attributes[type.key])) {
                        invalidAttributes.push(type.key);
                        const newValue = CONVERT_INVALID_VALUE[type.type](type, attributes[type.key]);
                        if (newValue !== undefined) {
                            attributes[type.key] = newValue;
                        }
                        else if (type.key in attributes) {
                            delete attributes[type.key];
                        }
                    }
                }
                catch (e) {
                    console.warn(`ObservabilitySDK :: Fail to resolve required attributes for key[${request.key}]. Ignoring filter and proceeding`, e);
                }
            }
        }
        return await chain.doFilter(request, response);
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ('resolver' in filterConfig && typeof filterConfig.resolver === 'function') {
            this.validationConfig = filterConfig;
        }
    }
}
function stringValidation(dataType, labelValue) {
    const type = dataType;
    const value = labelValue;
    const minLength = type.minLength || DEFAULT_MIN_LENGTH;
    const maxLength = type.maxLength || DEFAULT_MAX_LENGTH;
    return !!value && value.length >= minLength && value.length <= maxLength;
}
function booleanValidation(dataType, value) {
    return !!value && typeof value == "boolean";
}
function numberValidation(dataType, value) {
    return !!value && typeof value === "number";
}
function enumValidation(dataType, labelValue) {
    const type = dataType;
    if (!labelValue || typeof labelValue !== "string" || !type.values || type.values.length == 0) {
        return false;
    }
    const options = type.values.map(option => option.toLowerCase());
    return options.indexOf(labelValue.toString().toLowerCase()) > -1;
}
function stringInvalidConverter(dataType, currentValue) {
    const type = dataType;
    if (currentValue && typeof currentValue !== "string") {
        currentValue = currentValue.toString();
    }
    if (!currentValue || typeof currentValue !== "string" || currentValue.length < type.minLength) {
        if (type.default) {
            return type.default.toString();
        }
        return type.default;
    }
    if (currentValue.length > type.maxLength) {
        return currentValue.substring(0, type.maxLength);
    }
}
function booleanInvalidConverter(type, currentValue) {
    if (currentValue === undefined) {
        return type.default;
    }
    if (typeof currentValue === "string") {
        return currentValue.toLowerCase() === 'true';
    }
    try {
        return Boolean(currentValue);
    }
    catch (e) {
        console.warn(`ObservabilitySDK :: Fail to parse value ${currentValue} to boolean`, e);
        return false;
    }
}
function numberInvalidConverter(type) {
    return type.default;
}
function enumInvalidConverter(type, currentValue) {
    if (currentValue && typeof currentValue !== "string") {
        const value = currentValue.toString();
        if (enumValidation(type, value)) {
            return value;
        }
    }
    return type.default !== undefined ? type.default.toString() : type.default;
}
const VALIDATORS = {
    string: stringValidation,
    boolean: booleanValidation,
    number: numberValidation,
    enum: enumValidation
};
const CONVERT_INVALID_VALUE = {
    string: stringInvalidConverter,
    boolean: booleanInvalidConverter,
    number: numberInvalidConverter,
    enum: enumInvalidConverter
};

//FIXME Default type must be replaced by log once it get implemented
const DEFAULT_TYPE$1 = 'counter';
class MetricOptionsResolverFilter extends Filter {
    optionsResolverConfig;
    name() {
        /* istanbul ignore next */
        return 'MetricOptionsResolverFilter';
    }
    async doFilter(request, response, chain) {
        if (this.optionsResolverConfig && request.key) {
            try {
                const resolvedType = this.optionsResolverConfig.resolver(request.key);
                if (resolvedType) {
                    request.options = resolvedType;
                }
            }
            catch (e) {
                console.error(`ObservabilitySDK :: Fail to resolve options for key[${request.key}]. Ignoring filter and proceeding`, e);
                request.type = DEFAULT_TYPE$1;
            }
        }
        await chain.doFilter(request, response);
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ('resolver' in filterConfig && typeof filterConfig.resolver === 'function') {
            this.optionsResolverConfig = filterConfig;
        }
    }
}

class RequiredAttributesFilter extends Filter {
    requiredConfig;
    name() {
        /* istanbul ignore next */
        return 'RequiredAttributesFilter';
    }
    async doFilter(request, response, chain) {
        if (this.requiredConfig && request.key) {
            try {
                const attributes = request.attributes;
                const requiredAttributes = this.requiredConfig.resolver(request.key);
                if (requiredAttributes.length > 0 && (!attributes || Object.keys(attributes).length === 0)) {
                    return this.rejectAction(requiredAttributes, request.key);
                }
                else {
                    const missingAttributes = [];
                    for (const attribute of requiredAttributes) {
                        if (!(attribute in attributes)) {
                            missingAttributes.push(attribute);
                        }
                    }
                    if (missingAttributes.length > 0) {
                        return this.rejectAction(missingAttributes, request.key);
                    }
                }
            }
            catch (e) {
                console.warn(`ObservabilitySDK :: Fail to resolve required attributes for key[${request.key}]. Ignoring filter and proceeding`, e);
            }
        }
        return await chain.doFilter(request, response);
    }
    rejectAction(requiredAttributes, key) {
        console.warn(`ObservabilitySDK :: Missing attributes [${requiredAttributes}] for key[${key}]. Ignoring action`);
        return Promise.reject(new Error(`ObservabilitySDK :: Missing attributes [${requiredAttributes}] for key[${key}]. Ignoring action`));
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ('resolver' in filterConfig && typeof filterConfig.resolver === 'function') {
            this.requiredConfig = filterConfig;
        }
    }
}

//FIXME Default type must be replaced by log once it get implemented
const DEFAULT_TYPE = 'counter';
class TypeResolverFilter extends Filter {
    typeResolverConfig;
    name() {
        /* istanbul ignore next */
        return 'TypeResolverFilter';
    }
    async doFilter(request, response, chain) {
        if (this.typeResolverConfig && request.key) {
            try {
                const resolvedType = this.resolveType(request.key);
                request.type = resolvedType || DEFAULT_TYPE;
            }
            catch (e) {
                console.error(`ObservabilitySDK :: Fail to resolve type for key[${request.key}]. Ignoring filter and proceeding`, e);
                request.type = DEFAULT_TYPE;
            }
        }
        await chain.doFilter(request, response);
    }
    resolveType(key) {
        return this.typeResolverConfig.resolver(key);
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ('resolver' in filterConfig && typeof filterConfig.resolver === 'function') {
            this.typeResolverConfig = filterConfig;
        }
    }
}

const TOKEN_NOT_INITIALIZED = "404";
const TOKEN_EXPIRED = "403";
class Authentication {
    static INSTANCE;
    token;
    expiresAt;
    attempt = 0;
    //Component Config
    url = 'http://localhost:8080/auth';
    maxAttempts = 5;
    timeoutMillis = 5_000;
    backOffIntervalInMs = 500;
    setConfig(config) {
        if (config.maxAttempts) {
            this.maxAttempts = config.maxAttempts;
        }
        if (config.timeoutMillis) {
            this.timeoutMillis = config.timeoutMillis;
        }
        if (config.url) {
            this.url = config.url;
        }
        if (config.backOffIntervalInMs && !isNaN(config.backOffIntervalInMs)) {
            this.backOffIntervalInMs = new Number(config.backOffIntervalInMs);
        }
    }
    static getInstance() {
        if (!Authentication.INSTANCE) {
            Authentication.INSTANCE = new Authentication();
        }
        return Authentication.INSTANCE;
    }
    async authenticate() {
        this.attempt = 0;
        await this.authenticateOrRetry();
    }
    async authenticateOrRetry() {
        this.attempt = this.attempt + 1;
        try {
            return await this.doAuthenticate();
        }
        catch (e) {
            console.error('ObservabilitySDK :: Fail to authenticate in the remote server', e);
            if (this.attempt < this.maxAttempts) {
                console.info(`ObservabilitySDK :: Retrying to publich metrics in ${this.backOffIntervalInMs}ms`);
                await new Promise((r) => setTimeout(r, this.backOffIntervalInMs));
                return await this.authenticateOrRetry();
            }
            else {
                return Promise.reject(new Error(`Maximum authentication attempt reached. Attempts[${this.attempt}]`));
            }
        }
    }
    async doAuthenticate() {
        const response = await fetch(this.url, this.fetchConfig());
        if (!response.ok) {
            throw new Error(`Response status: ${response.status}`);
        }
        const jwt = await response.json();
        this.token = jwt.token;
        const expiration = new Date();
        expiration.setSeconds(expiration.getSeconds() + jwt.expires_in);
        this.expiresAt = expiration;
    }
    fetchConfig() {
        /* istanbul ignore next */
        return {
            mode: 'cors',
            method: 'GET',
            signal: AbortSignal.timeout(this.timeoutMillis)
        };
    }
    getToken() {
        return new Promise((resolve, reject) => {
            const token = this.token;
            if (token) {
                if (this.isValid()) {
                    resolve(token);
                    return;
                }
                else {
                    reject(new Error(TOKEN_EXPIRED));
                    return;
                }
            }
            reject(new Error(TOKEN_NOT_INITIALIZED));
        });
    }
    isValid() {
        return !this.isExpired();
    }
    isExpired() {
        return (!!this.expiresAt &&
            (this.expiresAt.getTime() - new Date().getTime()) <= 0);
    }
}
async function authenticate() {
    try {
        return await Authentication.getInstance().getToken();
    }
    catch (err) {
        try {
            //@ts-expect-error Any exception will be catched if the error doesn't have a messagfe
            if (err.message === TOKEN_NOT_INITIALIZED || err.message === TOKEN_EXPIRED) {
                await Authentication.getInstance().authenticate();
                return await Authentication.getInstance().getToken();
            }
            return new Promise((_resolve, reject) => {
                reject(err);
            });
        }
        catch (e) {
            return new Promise((_resolve, reject) => {
                reject(e);
            });
        }
    }
}

const AUTHORIZATION_HEADER = 'Authorization';
const BEARER_HEADER_VALUE = 'Bearer';
class JWTSecurityFilter extends Filter {
    securityConfig;
    name() {
        /* istanbul ignore next */
        return 'JWTSecurityFilter';
    }
    async doFilter(request, response, chain) {
        try {
            const token = await authenticate();
            if (token) {
                request.headers[AUTHORIZATION_HEADER] = `${BEARER_HEADER_VALUE} ${token}`;
            }
            return await chain.doFilter(request, request);
        }
        catch (e) {
            return Promise.reject(e);
        }
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ("url" in filterConfig || "maxAttempts" in filterConfig || "timeoutMillis" in filterConfig || "backOffIntervalInMs" in filterConfig) {
            this.securityConfig = {
                url: filterConfig.url,
                maxAttempts: filterConfig.maxAttempts,
                timeoutMillis: filterConfig.timeoutMillis,
                backOffIntervalInMs: filterConfig.backOffIntervalInMs
            };
            Authentication.getInstance().setConfig(this.securityConfig);
        }
    }
}

const RETRY_CONTEXT_KEY = '$RetryFilter$Context$';
const DEFAULT_BACKOFF_INTERVAL_IN_MS = 750;
const DEFAULT_MAX_ATTEMPTS = 5;
class RetryFilter extends Filter {
    retryConfig = {
        backOffIntervalInMs: DEFAULT_BACKOFF_INTERVAL_IN_MS,
        maxAttempts: DEFAULT_MAX_ATTEMPTS
    };
    name() {
        /* istanbul ignore next */
        return 'RetryFilter';
    }
    async doFilter(request, response, chain) {
        if (!(RETRY_CONTEXT_KEY in request.context)) {
            request.context[RETRY_CONTEXT_KEY] = {
                attempts: 0
            };
        }
        const context = request.context[RETRY_CONTEXT_KEY];
        context.attempts++;
        try {
            await chain.doFilter(request, response);
        }
        catch (e) {
            console.error('ObservabilitySDK :: Fail to publish metric to the remote server', e);
            if (context.attempts > this.retryConfig.maxAttempts) {
                console.warn(`ObservabilitySDK :: Tried to publish metrics ${this.retryConfig.maxAttempts} times. Registry is being dropped`);
                return Promise.reject(e);
            }
            console.info(`ObservabilitySDK :: Retrying to publich metrics in ${this.retryConfig.backOffIntervalInMs}ms`);
            await new Promise((r) => setTimeout(r, this.retryConfig.backOffIntervalInMs));
            await this.doFilter(request, request, chain);
        }
    }
    init(filterConfig) {
        super.init(filterConfig);
        this.retryConfig = {
            backOffIntervalInMs: (filterConfig.backOffIntervalInMs) ? filterConfig.backOffIntervalInMs : DEFAULT_BACKOFF_INTERVAL_IN_MS,
            maxAttempts: (filterConfig.maxAttempts) ? filterConfig.maxAttempts : DEFAULT_MAX_ATTEMPTS
        };
    }
}

class HTTPExporter {
    config;
    filters = [];
    chain;
    addFilter(filter) {
        this.filters.push(filter);
        this.createChain();
    }
    createChain() {
        this.chain = FilterChainBuilder.builder()
            .withFilters(this.filters)
            .addFilter(new HttpExporterFilter(this))
            .build();
    }
    shutdown() {
        //No Implementation required
    }
    async publish(registry) {
        if (this.chain && registry && Object.keys(registry.getMetricRegistry()).length > 0) {
            await this.chain.doFilter({
                headers: this.getDefaultHeaders(),
                registry: registry,
                context: {}
            }, {});
        }
    }
    getDefaultHeaders() {
        if (this.config && this.config.headers) {
            return this.config.headers;
        }
        return {};
    }
    setConfig(config) {
        if (config) {
            this.config = config;
        }
    }
    getConfig() {
        return this.config;
    }
}
class HttpExporterFilter extends Filter {
    exporter;
    constructor(exporter) {
        super();
        this.exporter = exporter;
    }
    family() {
        return 'http';
    }
    order() {
        return Number.MAX_SAFE_INTEGER;
    }
    name() {
        return 'HttpExporterFilter';
    }
    async doFilter(request, response, chain) {
        await this.exporter.doPublish(request);
        return chain.doFilter(request, response);
    }
}

function parseMetricRegistry(registry) {
    return {
        resourceMetrics: [
            parseMetricResourceMetrics(registry)
        ]
    };
}
function parseMetricResourceMetrics(registry) {
    if (!registry) {
        return {
            resource: buildMetricResource(),
            scopeMetrics: []
        };
    }
    const instance = registry.getMetricRegistry();
    return {
        resource: buildMetricResource(),
        scopeMetrics: [parseScopeMetrics(instance)]
    };
}
function buildMetricResource() {
    return {
        attributes: buildResourceAttributes(),
        droppedAttributesCount: 0
    };
}
function buildServiceNameAttribute() {
    return {
        key: 'service.name',
        value: {
            stringValue: ObservabilitySDK.getSystemName()
        }
    };
}
function buildSdkLanguage() {
    return {
        key: 'telemetry.sdk.language',
        value: {
            stringValue: 'webjs'
        }
    };
}
function buildSdkName() {
    return {
        key: 'telemetry.sdk.name',
        value: {
            stringValue: 'ObservabilitySDK'
        }
    };
}
function buildSdkVersion() {
    return {
        key: 'telemetry.sdk.version',
        value: {
            stringValue: '0.4.0' //Make it dynamic
        }
    };
}
function buildResourceAttributes() {
    return [
        buildServiceNameAttribute(),
        buildSdkLanguage(),
        buildSdkName(),
        buildSdkVersion()
    ];
}
function parseScopeMetrics(registry) {
    return {
        scope: buildScope(),
        metrics: buildMetric(registry)
    };
}
function buildScope() {
    return {
        name: ObservabilitySDK.getSystemName(),
        version: ObservabilitySDK.getVersion()
    };
}
function buildMetric(registry) {
    return Object.keys(registry)
        .map(key => registry[key])
        .filter(metric => !!metric && metric.getName() && metric.getValue().isInitialized())
        .map(metric => parseMetric(metric));
}
const METRIC_PARSERS = {
    histogram: parseHistogramMetric,
    gauge: parseGaugeMetric,
    counter: parseCounterMetric
};
function parseMetric(metric) {
    return METRIC_PARSERS[metric.getType()](metric);
}
function parseBaseMetric(metric) {
    return {
        name: metric.getName(),
        description: metric.getOptions()?.description,
        unit: metric.getOptions()?.unit,
    };
}
const AGGREGATION_TEMPORALITY_DELTA = 1;
function parseHistogramMetric(metric) {
    const value = metric.getValue();
    return {
        ...parseBaseMetric(metric),
        histogram: {
            dataPoints: [
                {
                    startTimeUnixNano: dateToUnixNano(metric.getValue().getRegisteredAt()),
                    timeUnixNano: dateToUnixNano(metric.getValue().getRegisteredAt()),
                    attributes: parseLabels(metric.getLabels()),
                    bucketCounts: value.getBuckets(),
                    explicitBounds: value.getBoundaries(),
                    count: value.getCount(),
                    sum: value.getSum(),
                    min: value.getMin() || 0,
                    max: value.getMax()
                }
            ],
            isMonotonic: true,
            aggregationTemporality: AGGREGATION_TEMPORALITY_DELTA
        }
    };
}
function parseGaugeMetric(metric) {
    const value = metric.getValue();
    return {
        ...parseBaseMetric(metric),
        gauge: {
            dataPoints: [
                {
                    timeUnixNano: dateToUnixNano(metric.getValue().getRegisteredAt()),
                    attributes: parseLabels(metric.getLabels()),
                    asDouble: value.getValue()
                }
            ]
        }
    };
}
function parseCounterMetric(metric) {
    const value = metric.getValue();
    return {
        ...parseBaseMetric(metric),
        sum: {
            dataPoints: [
                {
                    startTimeUnixNano: dateToUnixNano(metric.getValue().getRegisteredAt()),
                    timeUnixNano: dateToUnixNano(metric.getValue().getRegisteredAt()),
                    attributes: parseLabels(metric.getLabels()),
                    asDouble: value.getValue()
                }
            ],
            isMonotonic: true,
            aggregationTemporality: AGGREGATION_TEMPORALITY_DELTA
        }
    };
}
function dateToUnixNano(date) {
    return date.getTime() * 1000000;
}
function parseLabels(labels) {
    if (!labels) {
        return [];
    }
    return Object.keys(labels)
        .filter(key => !!labels[key])
        .map(key => {
        const value = labels[key];
        return {
            key: key,
            value: {
                stringValue: (!(typeof value === 'boolean') && !(typeof value === 'number')) ? value : undefined,
                boolValue: (typeof value === 'boolean') ? value : undefined,
                doubleValue: (typeof value === 'number') ? value : undefined,
            }
        };
    });
}

const DEFAULT_TIMEOUT = 5_000;
class OpenTelemetryMetricsExporter extends HTTPExporter {
    async doPublish(request) {
        if (!this.config) {
            console.warn(`ObservabilitySDK :: No URL defined. Dropping registry`);
            return new Promise(resolve => resolve());
        }
        const response = await fetch(this.config.url, this.buildOptions(request));
        if (response.ok) {
            return Promise.resolve();
        }
        return Promise.reject(`ObservabilitySDK :: Fail to publish metric to the remote server status[${response.status}]`);
    }
    buildOptions(request) {
        return {
            method: 'POST',
            body: JSON.stringify(this.convertToOtel(request.registry)),
            headers: {
                ...request.headers,
                'content-type': 'application/json'
            },
            keepalive: true,
            signal: AbortSignal.timeout(this.config?.timeoutMillis || DEFAULT_TIMEOUT)
        };
    }
    convertToOtel(registry) {
        return parseMetricRegistry(registry);
    }
}

const FILTER_TYPES = {
    'retryfilter': retryFilterExporter,
    'jwtsecurityfilter': jwtSecurityFilter,
    'requiredattributesfilter': requiredAttributesFilter,
    'metricoptionsresolverfilter': metricOptionsResolverFilter,
    'typeresolverfilter': typeResolverFilter,
    'datavalidationfilter': dataValidationFilter
};
/**
 * @param exporterFilter
 * @returns a instance of a filter based on the configuration
 * and true if it as correctly created. The boolean will return
 * as false if the creation fails.
 */
function filterFactory(context, spec) {
    if (spec.definition.type === 'filter') {
        return createInstance(spec.definition, context);
    }
    if (spec.definition.type === 'ref') {
        return createInstanceFromReference(context, spec.definition);
    }
    return [undefined, false];
}
function createInstance(spec, context) {
    try {
        if (spec.implementation && spec.implementation.toLowerCase() in FILTER_TYPES) {
            const filter = FILTER_TYPES[spec.implementation.toLowerCase()](context);
            filter.init({
                ...spec.config,
                ...{
                    family: (spec.config.family && typeof spec.config.family === 'string') ? spec.config.family : 'orphan',
                    order: (spec.config.order && isNumber(spec.config.order)) ? parseInt(spec.config.order.toString()) : Number.MIN_SAFE_INTEGER
                }
            });
            return [filter, true];
        }
    }
    catch (e) {
        console.error(`ObservabilitySDK :: Fail to create filter ${spec.implementation}`, e);
    }
    return [undefined, false];
}
function createInstanceFromReference(context, spec) {
    if (context.specs && spec.ref in context.specs) {
        const reference = context.specs[spec.ref];
        if ('definition' in reference) {
            const dataType = reference;
            if (dataType.definition.type == 'filter') {
                return createInstance(dataType.definition, context);
            }
        }
    }
    return [undefined, false];
}
function retryFilterExporter() {
    return new RetryFilter();
}
function jwtSecurityFilter() {
    return new JWTSecurityFilter();
}
function requiredAttributesFilter(context) {
    const filter = new RequiredAttributesFilter();
    filter.init({
        resolver: (key) => {
            if (!context.specs || !(key in context.specs)) {
                return [];
            }
            if ('labels' in context.specs[key]) {
                const labels = context.specs[key].labels;
                return Object.keys(labels)
                    .filter(label => !!labels[label].definition && !!labels[label].definition.required);
            }
            return [];
        },
        family: 'hook',
        order: -1 //Temp Value
    });
    return filter;
}
function metricOptionsResolverFilter(context) {
    const filter = new MetricOptionsResolverFilter();
    filter.init({
        resolver: (key) => {
            if (!context.specs || !(key in context.specs)) {
                return {};
            }
            if ('config' in context.specs[key]) {
                return context.specs[key].config;
            }
            return {};
        },
        family: 'hook',
        order: -1 //Temp Value
    });
    return filter;
}
function typeResolverFilter(context) {
    const filter = new TypeResolverFilter();
    filter.init({
        resolver: (key) => {
            if (!context.specs || !(key in context.specs)) {
                return undefined;
            }
            return context.specs[key].type || undefined;
        },
        family: 'hook',
        order: -1 //Temp Value
    });
    return filter;
}
function dataValidationFilter(context) {
    const filter = new DataValidationFilter();
    filter.init({
        resolver: (key) => {
            if (!context.specs || !(key in context.specs)) {
                return [];
            }
            if ('labels' in context.specs[key]) {
                const labels = context.specs[key].labels;
                return Object.keys(labels)
                    .map(label => {
                    return {
                        ...labels[label].definition,
                        key: label,
                    };
                });
            }
            return [];
        },
        family: 'hook',
        order: -1 //Temp Value
    });
    return filter;
}
function isNumber(value) {
    return ((value != null) &&
        (value !== '') &&
        !isNaN(Number(value.toString())));
}

const EXPORTER_TYPES = {
    opentelemetrymetricsexporter: createOtelMetricsExporter
};
class SDKConfigMerger extends ConfigMerger {
    apply(context, config) {
        ObservabilitySDK.setConfig({
            server: {
                url: config.server.url,
                headers: config.server.headers,
                maxRetry: config.server.maxRetry,
                timeoutMillis: config.server.timeoutMillis,
            },
            exportIntervalMillis: config.exportIntervalMillis,
            registryBuilder: this.registryBuilder(config)
        });
        for (const exporter of config.exporters) {
            this.addExporter(context, exporter);
        }
        if (config.dynamicHook) {
            for (const filter of config.dynamicHook.filters) {
                this.addDynamicHookFilter(context, filter);
            }
        }
    }
    registryBuilder(config) {
        if ("SimpleRegistry".toUpperCase() === config.registry.toUpperCase()) {
            return simpleRegistryBuilder;
        }
        return simpleRegistryBuilder;
    }
    shouldApply() {
        return true;
    }
    extract(config) {
        return config.sdk;
    }
    isValid(config) {
        return "sdk" in config;
    }
    merge(current, loaded) {
        if (!current.sdk) {
            current.sdk = loaded;
            current.sdk.exporters = this.removeInvalidExporter(loaded.exporters);
            return;
        }
        super.overrideNestedIfNotEmpty('server', ['url', 'headers', 'maxRetry', 'timeoutMillis'], current.sdk, loaded);
        super.overrideNestedIfNotEmpty('system', ['name', 'version'], current.sdk, loaded);
        super.overrideIfNotEmpty('exportIntervalMillis', current.sdk, loaded);
        super.overrideIfNotEmpty('registry', current.sdk, loaded);
        if (!current.sdk.exporters) {
            current.sdk.exporters = [];
        }
        const validExporters = this.removeInvalidExporter(loaded.exporters);
        const idFromLoadedExporters = validExporters.filter(exporter => !!exporter.id).map(exporter => exporter.id);
        if (idFromLoadedExporters && idFromLoadedExporters.length > 0 && current.sdk.exporters.length > 0) {
            current.sdk.exporters = current.sdk.exporters.filter(exporter => !exporter.id || idFromLoadedExporters.indexOf(exporter.id) === -1);
        }
        current.sdk.exporters = current.sdk.exporters.concat(validExporters);
    }
    removeInvalidExporter(exporters) {
        return exporters
            .filter(exporter => exporter && "type" in exporter && exporter.type.toLowerCase() in EXPORTER_TYPES);
    }
    addExporter(context, exporter) {
        if (exporter && exporter.type && exporter.type.toLowerCase() in EXPORTER_TYPES) {
            try {
                const observabilityExporter = EXPORTER_TYPES[exporter.type.toLowerCase()](context, exporter.config);
                if (exporter.filters && exporter.filters.length > 0) {
                    for (const spec of exporter.filters) {
                        const [filter, created] = filterFactory(context, spec);
                        if (created) {
                            observabilityExporter.addFilter(filter);
                        }
                    }
                }
                if (observabilityExporter) {
                    ObservabilitySDK.addExporter(observabilityExporter);
                }
            }
            catch (e) {
                console.warn(`ObservabilitySDK :: Fail to build exporter [${e}]`, e);
            }
        }
    }
    addDynamicHookFilter(context, spec) {
        try {
            const [filter, created] = filterFactory(context, spec);
            if (created) {
                DynamicHook.addFilter(filter);
            }
        }
        catch (e) {
            console.warn(`ObservabilitySDK :: Fail to build exporter [${e}]`, e);
        }
    }
}
function createOtelMetricsExporter(context, attributes) {
    const exporter = new OpenTelemetryMetricsExporter();
    const config = {
        url: context.sdk?.server.url || '',
        headers: {},
        maxRetry: context.sdk?.server.maxRetry || 0,
        timeoutMillis: context.sdk?.server.timeoutMillis || 5_000,
        backOffIntervalInMs: 500
    };
    if (attributes) {
        if (attributes.url && typeof attributes.url === 'string') {
            config.url = attributes.url;
        }
        if (attributes.maxRetry && typeof attributes.maxRetry === 'number' && !isNaN(attributes.maxRetry)) {
            config.maxRetry = attributes.maxRetry;
        }
        if (attributes.timeoutMillis && typeof attributes.timeoutMillis === 'number' && !isNaN(attributes.timeoutMillis)) {
            config.timeoutMillis = attributes.timeoutMillis;
        }
        if (attributes.backOffIntervalInMs && typeof attributes.backOffIntervalInMs === 'number' && !isNaN(attributes.backOffIntervalInMs)) {
            config.backOffIntervalInMs = attributes.backOffIntervalInMs;
        }
        if ('headers' in attributes) {
            config.headers = {
                ...config.headers,
                ...attributes.headers
            };
        }
    }
    if (context.sdk && 'headers' in context.sdk.server) {
        config.headers = {
            ...config.headers,
            ...context.sdk.server.headers
        };
    }
    exporter.setConfig(config);
    return exporter;
}
function simpleRegistryBuilder() {
    return new SimpleRegistry();
}

class MetricSpecParser {
    parse(config, spec) {
        const labels = {};
        if (spec.labels) {
            for (const key of Object.keys(spec.labels)) {
                labels[key] = {
                    type: 'type',
                    definition: spec.labels[key]
                };
            }
        }
        return {
            type: spec.type,
            config: {
                description: spec?.config?.description,
                unit: spec?.config?.unit
            },
            labels: loadSpec(config, labels)
        };
    }
}

const DEFAULT_BOUNDARIES = [0, 5, 10, 25, 50, 75, 100, 250, 500, 750, 1000, 2500, 5000, 7500, 10000];
class HistogramSpecParser extends MetricSpecParser {
    parse(config, spec) {
        const metricSpec = super.parse(config, spec);
        metricSpec.config['boundaries'] = this.resolveBoundaries(spec.config);
        return metricSpec;
    }
    resolveBoundaries(spec) {
        if (spec.boundaries && Array.isArray(spec.boundaries) && spec.boundaries.length > 0) {
            return spec.boundaries;
        }
        return DEFAULT_BOUNDARIES;
    }
}

class CounterSpecParser extends MetricSpecParser {
}

class GaugeSpecParser extends MetricSpecParser {
}

class StringDataTypeParser {
    parse(_config, definition) {
        return {
            type: 'string',
            maxLength: definition.maxLength,
            minLength: definition.minLength,
            required: definition.required,
            default: definition.default
        };
    }
}

const LAZY_PREFIX = '$$lazy';
class RefDataTypeParser {
    parse(config, spec, type) {
        if (spec.ref in config) {
            const definition = config[spec.ref].definition;
            return {
                ...definition,
                ...spec,
                type: definition.type
            };
        }
        return this.lazyParse(config, spec, type);
    }
    lazyParse(config, spec, type) {
        if (!(LAZY_PREFIX in config)) {
            config[LAZY_PREFIX] = {
                type: 'lazyloading',
                pending: {},
            };
        }
        const pending = config[LAZY_PREFIX].pending;
        if (!(spec.ref in pending)) {
            pending[spec.ref] = [];
        }
        pending[spec.ref].push({
            type: type,
            source: spec
        });
        return {
            type: 'lazy',
            ref: spec.ref
        };
    }
}

class BooleanDataTypeParser {
    parse(_config, definition) {
        return {
            type: 'bool',
            default: definition.default,
            required: definition.required
        };
    }
}

class NumberDataTypeParser {
    parse(_config, definition) {
        return {
            type: 'number',
            required: definition.required,
            default: definition.default
        };
    }
}

class EnumDataTypeParser {
    parse(_config, definition) {
        return {
            type: 'enum',
            values: definition.values,
            required: definition.required,
            default: definition.default
        };
    }
}

class FilterDataTypeParser {
    parse(_config, definition) {
        return {
            type: 'filter',
            implementation: definition.implementation,
            config: definition.config
        };
    }
}

const DATA_TYPES = {
    string: new StringDataTypeParser(),
    enum: new EnumDataTypeParser(),
    number: new NumberDataTypeParser(),
    boolean: new BooleanDataTypeParser(),
    ref: new RefDataTypeParser(),
    filter: new FilterDataTypeParser()
};
class DataTypeSpecParser {
    parse(config, spec) {
        const type = {
            type: 'type'
        };
        type.definition = this.definitionFactory(spec).parse(config, spec.definition, type);
        return type;
    }
    definitionFactory(spec) {
        const key = spec.definition.type.toLowerCase();
        if (key in DATA_TYPES) {
            return DATA_TYPES[spec.definition.type.toLowerCase()];
        }
        return DATA_TYPES.string;
    }
}

class ExternalSpecParser {
    parse() {
        throw new Error("Method not implemented.");
    }
}

const SPECS = {
    histogram: new HistogramSpecParser(),
    counter: new CounterSpecParser(),
    gauge: new GaugeSpecParser(),
    type: new DataTypeSpecParser(),
    external: new ExternalSpecParser()
};

class SpecConfigMerger extends ConfigMerger {
    shouldApply() {
        return false;
    }
    isValid(config) {
        return "specs" in config;
    }
    extract(config) {
        return config.specs;
    }
    apply() {
        //No Implementation
    }
    merge(current, loaded) {
        if (!current.specs) {
            current.specs = {
                [LAZY_PREFIX]: {
                    type: 'lazy',
                    pending: {}
                }
            };
        }
        current.specs = {
            ...current.specs,
            ...loadSpec(current.specs, loaded)
        };
        this.lazyResolver(current.specs);
    }
    lazyResolver(specs) {
        const lazy = specs[LAZY_PREFIX];
        if (lazy && lazy.pending) {
            for (const key of Object.keys(lazy.pending)) {
                if (key in specs) {
                    const definition = specs[key].definition;
                    for (const spec of lazy.pending[key]) {
                        spec.type.definition = {
                            ...definition,
                            ...spec.source,
                            ...{ type: definition.type }
                        };
                    }
                    delete lazy.pending[key];
                }
            }
        }
    }
}
function loadSpec(context, specs) {
    const loadedSpecs = {};
    for (const key of Object.keys(specs)) {
        if (!key || key.substring(0, 1) === "$") {
            continue;
        }
        const value = specs[key];
        if (value.type in SPECS) {
            loadedSpecs[key] = SPECS[value.type].parse(context, value);
        }
        else {
            console.warn(`ObservabilitySDK :: Spec type[${value.type}] not found`);
        }
    }
    return loadedSpecs;
}

var Status;
(function (Status) {
    Status[Status["Pending"] = 0] = "Pending";
    Status[Status["Processing"] = 1] = "Processing";
    Status[Status["Loaded"] = 2] = "Loaded";
    Status[Status["BackingOff"] = 3] = "BackingOff";
    Status[Status["Failed"] = 4] = "Failed";
    Status[Status["Ingested"] = 5] = "Ingested";
})(Status || (Status = {}));
class RemoteConfigLoader {
    urls = [];
    backOffInteralInSeconds = 10;
    //5 Seconds
    serverTimeoutInMs = 5_000;
    maxAttempts = 5;
    retryScheduler;
    load(url, onSuccess) {
        if (!url || !onSuccess) {
            console.error(`ObservabilitySDK :: Fail to start the loading process url[${url}] or callback[${onSuccess}] wasn't defined`);
        }
        else {
            this.urls.push({
                url: url,
                status: Status.Pending,
                createaAt: new Date(),
                attempt: 0,
                onSuccess: onSuccess
            });
        }
        this.process();
    }
    process() {
        if (!this.urls || this.urls.length == 0) {
            console.warn(`ObservabilitySDK :: No URLS to be loaded`);
            return;
        }
        for (const url of this.urls) {
            if (url.status == Status.Pending ||
                (url.status == Status.BackingOff &&
                    (!url.retryAt ||
                        url.retryAt.getTime() <= new Date().getTime()))) {
                this.doProcess(url);
                return;
            }
        }
        console.debug(`ObservabilitySDK :: All URLs have been loaded`);
    }
    doProcess(url) {
        url.attempt++;
        url.status = Status.Processing;
        try {
            fetch(url.url, this.fetchConfig())
                .then((value) => this.loadCompleted(url, value), (reason) => this.loadFailed(url, reason))
                .catch((reason) => this.loadFailed(url, reason));
        }
        catch (e) {
            this.loadFailed(url, e);
        }
    }
    async loadCompleted(url, response) {
        try {
            url.status = Status.Loaded;
            if (!response.ok) {
                this.loadFailed(url, `${response.status} - ${response.statusText}`);
                return;
            }
            const config = await response.text();
            url.onSuccess(config);
            url.status = Status.Ingested;
            url.completedAt = new Date();
        }
        catch (e) {
            this.loadFailed(url, e);
        }
    }
    loadFailed(url, reason) {
        console.debug(`ObservabilitySDK :: Fail to load config reason[${reason}]`);
        url.status = (url.attempt <= this.maxAttempts) ? Status.BackingOff : Status.Failed;
        if (url.status == Status.BackingOff) {
            const retryAt = new Date();
            retryAt.setSeconds(retryAt.getSeconds() + this.backOffInteralInSeconds);
            url.retryAt = retryAt;
        }
        if (!this.retryScheduler) {
            setTimeout(() => this.retry(), this.backOffInteralInSeconds * 1000);
        }
    }
    retry() {
        this.retryScheduler = undefined;
        this.process();
    }
    // Jest doesn't Support AbortController and AbortSignal.
    // It forces us to create a method that return the basic config,
    // allowing Jest to mock it while running tests
    fetchConfig() {
        /* istanbul ignore next */
        return {
            signal: AbortSignal.timeout(this.serverTimeoutInMs)
        };
    }
}

class ObservabilitySDKConfig {
    static instance;
    loader = new RemoteConfigLoader();
    config = {};
    mergers = [
        new SpecConfigMerger(),
        new SDKConfigMerger()
    ];
    /**
     * Parse a string to a list of URL to be loaded by the ObservabilitySDK.
     * Only https is supported
     * @param value List of URL divided by a delimiter
     * @param delimiter delimiter used to divide the urls. Default: ; (semicolon)
     * @returns
     */
    static parseStringToListOfUrls(value, delimiter = ';') {
        if (!value) {
            return [];
        }
        try {
            const urls = value.split(delimiter);
            const validUrls = [];
            for (const url of urls) {
                try {
                    const validUrl = new URL(url);
                    if (validUrl.protocol === 'https:') {
                        validUrls.push(validUrl.toString());
                    }
                }
                catch (e) {
                    console.warn(`ObservabilitySDK :: Fail parse url [${url}]. It will be dropped.`, e);
                }
            }
            return validUrls;
        }
        catch (e) {
            console.warn(`ObservabilitySDK :: Fail parse list of urls ${value}].`, e);
        }
        return [];
    }
    static loadFromUrls(urls) {
        const instance = ObservabilitySDKConfig.getInstance();
        if (!urls || !Array.isArray(urls) || urls.length == 0) {
            return instance;
        }
        for (const url of urls) {
            instance.loadFromUrl(url);
        }
        return instance;
    }
    static loadFromUrl(url) {
        return ObservabilitySDKConfig.getInstance().loadFromUrl(url);
    }
    static loadFromString(config) {
        return ObservabilitySDKConfig.getInstance().loadFromString(config);
    }
    static getInstance() {
        if (!ObservabilitySDKConfig.instance) {
            ObservabilitySDKConfig.instance = new ObservabilitySDKConfig();
        }
        return ObservabilitySDKConfig.instance;
    }
    loadFromUrl(url) {
        this.loader.load(url, ObservabilitySDKConfig.loadFromString);
        return this;
    }
    loadFromString(configAsString) {
        const config = JSON.parse(configAsString);
        this.mergeConfig(config);
        return this;
    }
    mergeConfig(config) {
        for (const merger of this.mergers) {
            try {
                if (merger.isValid(config)) {
                    const value = merger.extract(config);
                    merger.merge(this.config, value);
                    if (merger.shouldApply()) {
                        merger.apply(this.config, value);
                    }
                }
            }
            catch (e) {
                console.warn(`ObservabilitySDK :: Fail to merge configuration[${e}]`);
            }
        }
    }
    getConfig() {
        return this.config;
    }
}

export { ObservabilitySDKConfig };
//# sourceMappingURL=index.esm.js.map
